/* Copyright (c) 2020-present CloudIO Inc */

import { ActionItemType } from 'componentregistry/registry/Base.type';
import { Platform } from '../../platform/Platform';
import {
  ActionManager,
  ActionManagerFn,
  AliasType,
} from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionQueryCalendar';

const ActionQueryCalendarManager: ActionManagerFn<'ActionQueryCalendar'> = (
  platform: Platform,
): ActionManager<'ActionQueryCalendar'> => {
  return {
    ...baseActionManager(platform, 'ActionQueryCalendar'),
    defaultValue,
    props: {
      ...baseActionProps,
      calendarItemId: {
        label: 'Calendar Item ID',
        type: 'ItemID',
        initialFilter: 'Calendar',
        validateItemId: (value, { pageId, appUid, itemId }) => {
          const item = platform.getItem(appUid, pageId, value);
          if (item?.type !== 'Calendar') {
            return `Item id '${value}' doesn't belong to a Calendar`;
          }
          return '';
        },
      },
      fromDate: {
        label: 'Provide a startDate for the calendar',
        type: 'expression',
        returnType: async () => 'string',
        required: true,
      },
      endDate: {
        label: 'Provide a endDate for the calendar',
        type: 'expression',
        returnType: async () => 'string',
        required: true,
      },
    },
    getDataSourceAliasProperty: (aliasType: AliasType) => {
      return aliasType === 'QUERY_FILTER'
        ? {
            aliasProp: 'comboDatasourceAlias',
            embeddedPageIdProp: 'embeddedPageId',
          }
        : undefined;
    },
    isChildAllowed: (page, item, childType) =>
      (
        [
          'ActionSetFilterCombiner',
          'ActionSetQueryFilter',
          'ActionSetSimpleQueryFilter',
        ] as ActionItemType[]
      ).includes(childType as ActionItemType),
    getDisplayText: (item) => item.props.calendarItemId,
    name: 'Query Calendar',
    icon: { iconName: 'search' },
    description: `**Overview**<br/>
The Query Calendar action initiates a data retrieval operation specifically for Calendar components. It allows the application to fetch and refresh the dataset attached to a calendar based on a specific time window and optional filtering criteria. This action is essential for managing how event data is loaded and displayed as users navigate through different time periods.

**Key Features**<br/>
* **Time-Bound Data Retrieval:** Explicitly requires start and end date parameters to define the query window, ensuring only relevant event data is fetched.
* **Expression-Based Dates:** Supports dynamic expressions for date inputs, allowing the query range to adjust automatically at runtime (e.g., calculating "Start of Month" or "Today").
* **Nested Filter Support:** Explicitly permits the inclusion of advanced filtering components—specifically ActionSetFilterCombiner, ActionSetQueryFilter, and ActionSetSimpleQueryFilter—as child items to refine the query results further.
* **Target Validation:** Includes strict type checking to ensure the action is only associated with valid Calendar components.

**Properties**<br/>
* **Calendar Item ID (Required):** The unique identifier of the Calendar component to be queried.
  * *Constraint:* The target must be of type Calendar.
* **Provide a startDate for the calendar (fromDate) (Required):** An expression that resolves to a string representing the beginning of the query range.
* **Provide a endDate for the calendar (endDate) (Required):** An expression that resolves to a string representing the cutoff point for the query range.

**Use Cases**<br/>
* **View Navigation:** Refreshing the calendar's data when a user switches views (e.g., from "Month" view to "Week" view) or navigates to a different date range.
* **Filtered Event Loading:** Fetching a subset of calendar events (e.g., "High Priority" meetings only) within a specific date range using nested query filters.
* **Dynamic Refreshes:** Reloading calendar data programmatically based on user interactions elsewhere in the application.`,
  };
};

export default ActionQueryCalendarManager;
